"use client";

import { useState, useRef } from "react";
import {
  Type,
  Square,
  Circle,
  Triangle,
  Minus,
  Image as ImageIcon,
  Trash2,
  Download,
  Palette,
  Presentation,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { InfoTooltip } from "@/components/ui/tooltip";

interface ProposalToolbarProps {
  onAddText: () => void;
  onAddShape: (type: "rect" | "circle" | "triangle" | "line") => void;
  onAddImage: (url: string) => void;
  onDelete: () => void;
  onSetBackground: (color: string) => void;
  onExportPptx: () => void;
  hasSelection: boolean;
  readOnly?: boolean;
}

// Palette de couleurs Logissimo pour les fonds
const PRESET_COLORS = [
  "#ffffff",  // blanc
  "#f8f9fa",  // gris très clair
  "#e9ecef",  // gris clair
  "#FFF5CC",  // laposte-yellow-light
  "#CCEFED",  // laposte-teal-light
  "#CCE0F0",  // laposte-blue-light
  "#FACCE6",  // laposte-magenta-light
  "#FFCC00",  // laposte-yellow
  "#00B2A9",  // laposte-teal
  "#003366",  // laposte-navy
];

export function ProposalToolbar({
  onAddText,
  onAddShape,
  onAddImage,
  onDelete,
  onSetBackground,
  onExportPptx,
  hasSelection,
  readOnly = false,
}: ProposalToolbarProps) {
  const [imageUrl, setImageUrl] = useState("");
  const [customColor, setCustomColor] = useState("#ffffff");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUrlSubmit = () => {
    if (imageUrl.trim()) {
      onAddImage(imageUrl.trim());
      setImageUrl("");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        onAddImage(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  if (readOnly) {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted/50 border-b">
        <span className="text-sm text-muted-foreground">Mode lecture seule</span>
        <Separator orientation="vertical" className="h-6" />
        <Button variant="outline" size="sm" onClick={onExportPptx}>
          <Presentation className="h-4 w-4 mr-2" />
          Exporter PPTX
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 p-2 bg-muted/50 border-b flex-wrap">
      {/* Text Tool */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onAddText}
        title="Ajouter du texte"
      >
        <Type className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Shape Tools */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" title="Ajouter une forme">
            <Square className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => onAddShape("rect")}>
            <Square className="h-4 w-4 mr-2" />
            Rectangle
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddShape("circle")}>
            <Circle className="h-4 w-4 mr-2" />
            Cercle
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddShape("triangle")}>
            <Triangle className="h-4 w-4 mr-2" />
            Triangle
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAddShape("line")}>
            <Minus className="h-4 w-4 mr-2" />
            Ligne
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Image Tool */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" title="Ajouter une image">
            <ImageIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">URL de l&apos;image</label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="https://..."
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleImageUrlSubmit();
                  }}
                />
                <Button size="sm" onClick={handleImageUrlSubmit}>
                  Ajouter
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-popover px-2 text-muted-foreground">ou</span>
              </div>
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleFileSelect}
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Choisir un fichier
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Background Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" title="Couleur de fond">
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="space-y-3">
            <label className="text-sm font-medium">Couleur de fond</label>
            <div className="grid grid-cols-5 gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  className="w-8 h-8 rounded border border-border hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  onClick={() => onSetBackground(color)}
                  title={color}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="color"
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                className="w-12 h-8 p-0 border-0"
              />
              <Input
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value)}
                placeholder="#ffffff"
                className="flex-1"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSetBackground(customColor)}
              >
                Appliquer
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Delete */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        disabled={!hasSelection}
        title="Supprimer la sélection"
        className="text-destructive hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <div className="flex-1" />

      {/* Export */}
      <Button variant="outline" size="sm" onClick={onExportPptx}>
        <Presentation className="h-4 w-4 mr-2" />
        Exporter PPTX
      </Button>
    </div>
  );
}
